import React, { useState } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import { toPercent } from "@helpers";
import { useChartColors } from "@hooks";
import { HalfDonutChart } from "./HalfDonutChart";
import { HalfChartDataItem } from "./useChartOptions";

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ theme, compact }) => ({
  width: "100%",
  height: compact ? "fit-content" : "100%",
  display: "grid",
  gridTemplateColumns: compact ? "1fr" : "1fr 1fr",
  gap: theme.spacing(compact ? 0 : 11),

  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const Circle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color: string }>(({ color }) => ({
  flexShrink: 0,
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: color,
}));

type Props = {
  data: HalfChartDataItem[];
  compact?: boolean;
};

export const HalfDonutChartWithData = ({ data, compact = false }: Props) => {
  const [activeItem, setActiveItem] = useState<string>();
  const isDownSM = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const colors = useChartColors("donutChart");
  const height = compact ? (isDownSM ? 100 : 150) : 150;

  return (
    <Container compact={compact}>
      <Box
        width="100%"
        height="100%"
        minHeight={height}
        sx={{ overflow: "hidden" }}
      >
        <AutoSizer>
          {({ width, height }) => {
            return (
              <Box width={width} height={height} sx={{ overflow: "hidden" }}>
                <HalfDonutChart
                  width={width}
                  height={height * (isDownSM ? 2 : 1.5)}
                  activeItem={activeItem}
                  setActiveItem={setActiveItem}
                  data={data}
                  colors={colors}
                />
              </Box>
            );
          }}
        </AutoSizer>
      </Box>

      <Box width="100%" minWidth={0}>
        {data.map((item, index) => (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            sx={{
              backgroundColor:
                item.name === activeItem ? "background.subtle" : "transparent",
              cursor: "pointer",
            }}
            gap={4}
            mb={2}
            onMouseOver={() => setActiveItem(item.name)}
            onMouseOut={() => setActiveItem(undefined)}
          >
            <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <Circle color={colors[index % colors.length]} />
              <Typography
                variant={compact ? "caption" : "body3"}
                color="textSecondary"
                noWrap
              >
                {item.name}
              </Typography>
            </Box>
            <Typography variant={compact ? "body3" : "body1"}>
              {toPercent(item.y)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Container>
  );
};
